import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { notificationDestination } from '../lib/notification-links.js';

const FILTERS = [['all', 'All'], ['unread', 'Unread'], ['action', 'Action needed']];
const PAGE_SIZE = 25;

function relativeTime(iso) {
  if (!iso) return '';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Notifications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedFilter = searchParams.get('filter');
  const filter = FILTERS.some(([id]) => id === requestedFilter) ? requestedFilter : 'all';
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const [data, setData] = useState({ notifications: [], unread_count: 0, action_needed_count: 0, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get(`/api/notifications?filter=${filter}&page=${page}&limit=${PAGE_SIZE}`));
      setError(null);
    } catch (err) {
      setError(err?.body?.error || err?.message || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  function chooseFilter(next) {
    setSearchParams(next === 'all' ? {} : { filter: next });
  }

  function choosePage(next) {
    const params = {};
    if (filter !== 'all') params.filter = filter;
    if (next > 1) params.page = String(next);
    setSearchParams(params);
  }

  function markOne(row) {
    if (row.read_at) return;
    setData((current) => ({
      ...current,
      unread_count: Math.max(0, current.unread_count - 1),
      notifications: current.notifications.map((item) => item.id === row.id ? { ...item, read_at: new Date().toISOString() } : item),
    }));
    api.post(`/api/notifications/${row.id}/read`, {}).catch(load);
  }

  function openRow(row) {
    markOne(row);
    const destination = notificationDestination(row);
    if (destination) navigate(destination);
  }

  async function markAll() {
    await api.post('/api/notifications/read-all', {});
    load();
  }

  return (
    <div className="min-h-screen bg-silver-a">
      <header className="border-b border-hunter bg-hunter-deep">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div><div className="font-display text-2xl font-black text-white">CCF Front Office</div><div className="mt-1 text-[0.65rem] font-bold uppercase tracking-[0.25em] text-mid-green">Notification history</div></div>
          <Link to="/" className="text-sm text-silver-a underline hover:text-white">← Front Office</Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="font-display text-2xl font-black text-silver-text">Notifications</h1><p className="mt-1 text-sm text-silver-mute">{data.unread_count} unread · {data.action_needed_count} need action</p></div>
          <button type="button" onClick={markAll} disabled={!data.unread_count} className="text-sm font-bold text-hunter underline disabled:no-underline disabled:opacity-40">Mark all read</button>
        </div>
        <div className="mb-4 flex gap-2" role="tablist" aria-label="Notification filter">
          {FILTERS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={filter === id} onClick={() => chooseFilter(id)} className={`rounded-full px-4 py-2 text-sm font-bold ${filter === id ? 'bg-hunter text-white' : 'border border-gray-300 bg-white text-hunter'}`}>{label}</button>)}
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading && <div className="p-10 text-center text-sm text-silver-mute">Loading…</div>}
          {!loading && error && <div className="p-4 text-sm text-red-700">{error}</div>}
          {!loading && !error && !data.notifications.length && <div className="p-10 text-center text-sm text-silver-mute">No notifications in this view.</div>}
          {!loading && !error && data.notifications.map((row) => (
            <div key={row.id} className={`flex items-stretch border-b border-gray-100 last:border-0 ${row.read_at ? '' : 'bg-sage-100/40'}`}>
              <button type="button" onClick={() => openRow(row)} className="min-w-0 flex-1 px-5 py-4 text-left hover:bg-silver-a/60">
                <span className="block text-base font-semibold text-silver-text">{row.title}</span>
                {row.body && <span className="mt-1 block text-sm text-silver-mute">{row.body}</span>}
                <span className="mt-2 block text-xs font-bold uppercase tracking-wide text-silver-mute">{row.actionable ? 'Action needed · ' : ''}{relativeTime(row.created_at)}</span>
              </button>
              {!row.read_at && <button type="button" onClick={() => markOne(row)} className="w-14 text-xl text-silver-mute hover:bg-silver-a/60 hover:text-mid-green" aria-label={`Mark ${row.title} as read`} title="Mark as read">✓</button>}
            </div>
          ))}
        </div>
        {data.pages > 1 && <div className="mt-5 flex items-center justify-between"><button type="button" disabled={page <= 1} onClick={() => choosePage(page - 1)} className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-hunter disabled:opacity-40">Previous</button><span className="text-sm text-silver-mute">Page {page} of {data.pages} · {data.total} notifications</span><button type="button" disabled={page >= data.pages} onClick={() => choosePage(page + 1)} className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-hunter disabled:opacity-40">Next</button></div>}
      </main>
    </div>
  );
}
